import React, { useState, useEffect } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps, MetricConfig } from "./types";
import { formatDateInTimezone } from "./utils";
import { InteractiveLineChart } from "../../../components/analytics";

/* eslint-disable camelcase */
const metricConfig: MetricConfig[] = [
    { key: "video_starts", name: "Video Starts", color: "#8884d8" },
    { key: "progress_25_pct", name: "25% Progress", color: "#ff7c7c" },
    { key: "progress_50_pct", name: "50% Progress", color: "#8b5cf6" },
    { key: "progress_75_pct", name: "75% Progress", color: "#06b6d4" },
    { key: "progress_90_pct", name: "90% Progress", color: "#10b981" },
    { key: "completions", name: "Completions", color: "#82ca9d" },
    { key: "unique_viewers", name: "Unique Viewers", color: "#ffc658" }
];

const initialVisibleMetrics: Record<string, boolean> = {
    video_starts: true,
    progress_25_pct: false,
    progress_50_pct: false,
    progress_75_pct: false,
    progress_90_pct: false,
    completions: true,
    unique_viewers: true
};
/* eslint-enable camelcase */

const DailyVideoEngagementOldWidget: React.FC<AnalyticWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions, // Accept but not used for line charts
    timezone
}) => {
    const [analyticsData, setAnalyticsData] = useState<Array<Record<string, any>> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);
            
            try {
                const response = await getAnalyticsWidget(
                    customerConfig,
                    "daily_video_engagement_old",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandId,
                    filters.deviceType
                );
                // Extract data from result key and ensure it's an array
                const data = (response as any)?.result;
                const dataArray = Array.isArray(data) ? data : [];
                setAnalyticsData(dataArray);
            } catch (err) {
                Logger.error("Error fetching daily video engagement data", err);
                setError("Failed to fetch daily video engagement data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters]);

    if (isLoading) {
        return (
            <div className="text-center" style={{ padding: "40px" }}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginBottom: "40px" }}>
                <h4>Daily Video Engagement (Old)</h4>
                <Alert variant="danger">{error}</Alert>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: "40px" }}>
            <h4>Daily Video Engagement (Old)</h4>
            
            {(!analyticsData || analyticsData.length === 0) ? (
                <Alert variant="info">No data available for the selected date range.</Alert>
            ) : (
                <>
                    <InteractiveLineChart
                        title=""
                        data={analyticsData}
                        xAxisDataKey="key"
                        metrics={metricConfig}
                        initialVisibleMetrics={initialVisibleMetrics}
                        theme={theme}
                        xAxisTickFormatter={(value: string) => formatDateInTimezone(value, timezone, "short")}
                        tooltipLabelFormatter={(value: string) => formatDateInTimezone(value, timezone, "short")}
                    />
                    <details>
                        <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "10px" }}>
                            View Raw Data
                        </summary>
                        <pre style={{ 
                            backgroundColor: "#f5f5f5", 
                            padding: "15px", 
                            borderRadius: "5px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(analyticsData, null, 2)}
                        </pre>
                    </details>
                </>
            )}
        </div>
    );
};

export default DailyVideoEngagementOldWidget;
